import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Combobox, Transition } from '@headlessui/react'
import { Check, ChevronUpDown } from 'components/icons/outline'

const SelectSearchable = ({
  filter = () => '',
  selection = [],
  isLoading,
  value,
  placeholder,
  keyValue = () => '',
  title = () => '',
  description = () => '',
  onChange = () => {},
  disabled = false,
  error,
  reverse = false,
  multiple = false,
  onSearchChange,
  serverSearch = false,
  searchValue,
  clearOnClose = true,
}) => {
  const isMultiple = Boolean(multiple)

  const getSafeKey = useCallback((row) => {
    if (row == null) return ''
    return String(keyValue(row) ?? '')
  }, [keyValue])

  const normalizeKeys = useCallback((nextValue) => {
    if (isMultiple) {
      if (!Array.isArray(nextValue)) return []
      return nextValue
        .map((item) => {
          if (item && typeof item === 'object') return getSafeKey(item)
          return String(item ?? '')
        })
        .filter(Boolean)
    }

    if (nextValue && typeof nextValue === 'object') {
      return getSafeKey(nextValue)
    }

    return String(nextValue ?? '')
  }, [getSafeKey, isMultiple])

  const [query, setQuery] = useState(searchValue ?? '')
  const [selectedCache, setSelectedCache] = useState(isMultiple ? [] : null)

  useEffect(() => {
    if (searchValue !== undefined) {
      setQuery(searchValue)
    }
  }, [searchValue])

  const selected = useMemo(() => {
    if (isMultiple) {
      const targetKeys = normalizeKeys(value)
      const matched = targetKeys
        .map((targetKey) =>
          selection.find((row) => getSafeKey(row) === targetKey) ||
          (Array.isArray(selectedCache)
            ? selectedCache.find((row) => getSafeKey(row) === targetKey)
            : null)
        )
        .filter(Boolean)

      return matched
    }

    const targetKey = normalizeKeys(value)
    const found = selection.find((row) => getSafeKey(row) === targetKey)
    return found || selectedCache || null
  }, [selection, value, selectedCache, getSafeKey, normalizeKeys, isMultiple])

  useEffect(() => {
    if (isMultiple) {
      const targetKeys = normalizeKeys(value)
      if (Array.isArray(selected) && selected.length) {
        setSelectedCache(selected)
      } else if (!targetKeys.length) {
        setSelectedCache([])
      }
      return
    }

    if (selected && getSafeKey(selected) === normalizeKeys(value)) {
      setSelectedCache(selected)
    } else if (!value) {
      setSelectedCache(null)
    }
  }, [selected, value, getSafeKey, normalizeKeys, isMultiple])

  const filtered = useMemo(() => {
    if (serverSearch) return selection

    const q = String(query ?? '').trim().toLowerCase()
    if (!q) return selection

    return selection.filter((row) =>
      String(filter(row) ?? '').toLowerCase().includes(q)
    )
  }, [query, selection, serverSearch, filter])

  const handleQueryChange = (val) => {
    if (searchValue === undefined) {
      setQuery(val)
    }
    if (typeof onSearchChange === 'function') {
      onSearchChange(val)
    }
  }

  const inputValue =
    String(query ?? '').length > 0
      ? query
      : ''

  const selectedLabel = useMemo(() => {
    if (isMultiple) {
      if (!Array.isArray(selected) || !selected.length) return ''
      return selected.map((row) => String(title(row) ?? '')).filter(Boolean).join(', ')
    }

    if (!selected) return ''
    return String(title(selected) ?? '')
  }, [isMultiple, selected, title])

  return (
    <Combobox
      value={selected}
      onChange={(next) => {
        setSelectedCache(next || (isMultiple ? [] : null))

        if (searchValue === undefined) {
          setQuery('')
        }

        if (typeof onSearchChange === 'function') {
          onSearchChange('')
        }

        onChange(
          isMultiple
            ? (Array.isArray(next) ? next.map((row) => getSafeKey(row)) : [])
            : getSafeKey(next),
          next
        )
      }}
      disabled={disabled}
      multiple={multiple}
      by={(a, b) => getSafeKey(a) === getSafeKey(b)}
    >
      <div className="relative mt-1">
        <div
          className={`${error ? 'border-red-200' : 'border-neutral-200'} ${
            !isLoading && inputValue ? '' : 'text-neutral-500'
          } ${disabled ? 'bg-neutral-50 opacity-75' : ''} w-full text-sm text-left transition border focus:outline-none rounded-xl hover:border-neutral-400 hover:ring-inset focus:border-neutral-400 focus:ring focus:ring-neutral-200`}
        >
          <Combobox.Input
            className="w-full text-sm border-none focus:ring-0 bg-none rounded-xl"
            placeholder={placeholder}
            onFocus={() => {
              if (searchValue === undefined) {
                setQuery('')
              }
              if (typeof onSearchChange === 'function') {
                onSearchChange('')
              }
            }}
            onChange={(e) => handleQueryChange(e.target.value)}
            displayValue={() => inputValue || selectedLabel}
          />

          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDown className="w-5 h-5 text-neutral-400" aria-hidden="true" />
          </Combobox.Button>
        </div>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => {
            if (clearOnClose) {
              if (searchValue === undefined) {
                setQuery('')
              }
              if (typeof onSearchChange === 'function') {
                onSearchChange('')
              }
            }
          }}
        >
          <Combobox.Options className="absolute z-10 w-full p-1 mt-1 overflow-auto text-base bg-white border shadow-lg rounded-xl max-h-60 border-neutral-200 focus:outline-none sm:text-sm">
            {filtered.length ? (
              filtered.map((row) => (
                <Combobox.Option
                  key={getSafeKey(row)}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 px-2 rounded-lg ${
                      active ? 'bg-neutral-100 text-neutral-900' : 'text-neutral-900'
                    } transition`
                  }
                  value={row}
                >
                  {({ selected }) => (
                    <>
                      <div className={`${reverse ? 'flex-col-reverse' : 'flex-col'} flex pr-6`}>
                        <span className="block truncate">{title(row)}</span>
                        <span className="block truncate text-xs text-neutral-600">
                          {description(row)}
                        </span>
                      </div>
                      <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <div
                          className={`${
                            selected ? 'bg-neutral-600' : 'border border-neutral-300'
                          } w-5 h-5 flex items-center justify-center text-white rounded-full`}
                        >
                          {selected && <Check className="w-4 h-4" stroke={1.5} aria-hidden="true" />}
                        </div>
                      </span>
                    </>
                  )}
                </Combobox.Option>
              ))
            ) : (
              <div className="relative px-4 py-2 text-xs transition rounded-lg cursor-pointer select-none text-neutral-500">
                <hr className="border-neutral-200" />
                <span className="absolute inset-0 flex justify-center">
                  <span className="px-2 bg-white">No Selection</span>
                </span>
              </div>
            )}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  )
}

export default SelectSearchable
